import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const defaultNotifications = {
  in_app_enabled: true,
  email_enabled: false,
  connection_health_enabled: true,
  cashflow_risk_enabled: true,
  card_bills_enabled: true,
  insights_enabled: true,
  weekly_review_enabled: true,
};

function buildFormFromResponse(data) {
  const household = data?.household || {};
  const preferences = data?.preferences || {};

  return {
    display_name: household.display_name || '',
    month_start_day: String(household.month_start_day || 1),
    monthly_safety_buffer: household.monthly_safety_buffer ?? 0,
    primary_income_amount: household.primary_income_amount ?? '',
    primary_income_day_of_month: household.primary_income_day_of_month ?? '',
    home_view: preferences.home_view || 'daily',
    weekly_review_day: String(preferences.weekly_review_day ?? 1),
    notifications_json: {
      ...defaultNotifications,
      ...(preferences.notifications_json || {}),
    },
  };
}

const reviewDayOptions = [
  { value: '0', label: 'Domingo' },
  { value: '1', label: 'Segunda' },
  { value: '2', label: 'Terca' },
  { value: '3', label: 'Quarta' },
  { value: '4', label: 'Quinta' },
  { value: '5', label: 'Sexta' },
  { value: '6', label: 'Sabado' },
];

const homeViewOptions = [
  { value: 'daily', label: 'Clareza diaria' },
  { value: 'cash', label: 'Caixa' },
  { value: 'cards', label: 'Cartoes' },
];

const notificationOptions = [
  { key: 'in_app_enabled', label: 'Inbox in-app', helper: 'Mantem o inbox nativo do modulo ativo.' },
  { key: 'email_enabled', label: 'Email preparado', helper: 'Prepara o canal de email para quando o provider for ligado.' },
  { key: 'connection_health_enabled', label: 'Saude de conexoes', helper: 'Consentimento, reauth, stale sync e backoff.' },
  { key: 'cashflow_risk_enabled', label: 'Risco de caixa', helper: 'Quedas de saldo e aperto no forecast curto.' },
  { key: 'card_bills_enabled', label: 'Faturas proximas', helper: 'Faturas que entram na janela curta de vencimento.' },
  { key: 'insights_enabled', label: 'Insights', helper: 'Desperdicio, recorrencia e pressao patrimonial.' },
  { key: 'weekly_review_enabled', label: 'Revisao semanal', helper: 'Lembrete da janela semanal de decisao.' },
];

const PersonalFinanceSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(buildFormFromResponse(null));

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/personal-finance/settings');
      setForm(buildFormFromResponse(response.data || null));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar ajustes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleNotificationToggle = (key) => {
    setForm((current) => ({
      ...current,
      notifications_json: {
        ...current.notifications_json,
        [key]: !current.notifications_json[key],
      },
    }));
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await axios.put('/api/personal-finance/settings', form);
      toast.success('Ajustes salvos');
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar ajustes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.02fr_0.98fr]">
      <div className="space-y-6">
        <div className="card">
          <div className="card-body space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Household</p>
              <h2 className="text-xl font-semibold text-slate-900">Ajustes base do copiloto</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="form-label">Nome do workspace</label>
                <input className="form-input" value={form.display_name} onChange={(event) => setForm({ ...form, display_name: event.target.value })} disabled={loading} />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="form-label">Dia de inicio do mes</label>
                  <input className="form-input" type="number" min="1" max="28" value={form.month_start_day} onChange={(event) => setForm({ ...form, month_start_day: event.target.value })} disabled={loading} />
                </div>
                <div>
                  <label className="form-label">Buffer de seguranca</label>
                  <input className="form-input" type="number" min="0" step="0.01" value={form.monthly_safety_buffer} onChange={(event) => setForm({ ...form, monthly_safety_buffer: event.target.value })} disabled={loading} />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="form-label">Renda principal</label>
                  <input className="form-input" type="number" min="0" step="0.01" value={form.primary_income_amount} onChange={(event) => setForm({ ...form, primary_income_amount: event.target.value })} disabled={loading} />
                </div>
                <div>
                  <label className="form-label">Dia do recebimento</label>
                  <input className="form-input" type="number" min="1" max="31" value={form.primary_income_day_of_month} onChange={(event) => setForm({ ...form, primary_income_day_of_month: event.target.value })} disabled={loading} />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="form-label">Visao padrao da home</label>
                  <select className="form-select" value={form.home_view} onChange={(event) => setForm({ ...form, home_view: event.target.value })} disabled={loading}>
                    {homeViewOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Dia da revisao semanal</label>
                  <select className="form-select" value={form.weekly_review_day} onChange={(event) => setForm({ ...form, weekly_review_day: event.target.value })} disabled={loading}>
                    {reviewDayOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <button className="btn-primary w-full" onClick={saveSettings} disabled={loading || saving}>
              {saving ? 'Salvando...' : 'Salvar ajustes'}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Por que isso existe</p>
              <h2 className="text-xl font-semibold text-slate-900">Decisoes do modelo</h2>
            </div>
            <div className="space-y-3 text-sm text-slate-600">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                O modulo pessoal nao nasce de plano de contas; ele nasce de household, renda, buffer, metas e dados conectados.
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                O dia da revisao e as preferencias de alerta entram no dominio para fechar o loop entre previsao, risco e acao.
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                Email ainda nao e disparado neste slice. O contrato existe para nao quebrar quando o provider entrar.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Alertas</p>
            <h2 className="text-xl font-semibold text-slate-900">Preferencias do usuario</h2>
          </div>

          <div className="rounded-3xl border border-emerald-100 bg-emerald-50/70 p-5 text-sm text-emerald-900">
            As preferencias abaixo sao do usuario atual dentro do bounded context pessoal. Isso evita misturar operacao do household com regras do financeiro corporativo.
          </div>

          <div className="space-y-3">
            {notificationOptions.map((item) => (
              <label key={item.key} className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                  checked={Boolean(form.notifications_json[item.key])}
                  onChange={() => handleNotificationToggle(item.key)}
                  disabled={loading}
                />
                <div>
                  <p className="font-medium text-slate-900">{item.label}</p>
                  <p className="mt-1 text-sm text-slate-500">{item.helper}</p>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalFinanceSettingsPage;
